/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils'
import {useForm} from 'react-hook-form'
import {formatPhoneNumber} from '@salesforce/retail-react-app/app/utils/phone-utils'

export default function useRegistrationFields({
    form: {
        control,
        formState: {errors},
        watch
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const unformatPhoneNumber = (value) => value.replace(/\D/g, '')
    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_registration_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_registration_fields.error.required_first_name'
                })
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_registration_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_registration_fields.error.required_last_name'
                })
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email',
                id: 'use_registration_fields.label.email'
            }),
            placeholder: 'example@email.com',
            type: 'email',
            autoComplete: 'new-email',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_registration_fields.error.required_email'
                }),
                pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address format.',
                        id: 'use_registration_fields.error.invalid_email'
                    })
                }
            },
            error: errors[`${prefix}email`],
            control
        },
        mobile: {
            name: `${prefix}mobile`,
            label: formatMessage({
                defaultMessage: 'Mobile Phone',
                id: 'use_registration_fields.label.mobile'
            }),
            type: 'tel',
            autoComplete: 'tel',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid mobile phone.',
                    id: 'use_registration_fields.error.required_mobile'
                }),
                pattern: {
                    value: /^1?[2-9][0-9]{2}[2-9][0-9]{6}$/,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid mobile phone.',
                        id: 'use_registration_fields.error.invalid_us_mobile'
                    })
                }
            },
            inputProps: ({value, onChange}) => ({
                inputMode: 'numeric',
                value: formatPhoneNumber(value),
                onChange(e) {
                    onChange(unformatPhoneNumber(e.target.value))
                }
            }),
            error: errors[`${prefix}mobile`],
            control
        },
        birthday: {
            name: `${prefix}birthday`,
            label: formatMessage({
                defaultMessage: 'Birthday (Optional)',
                id: 'use_registration_fields.label.birthday'
            }),
            placeholder: '+1',
            type: 'date',
            autoComplete: 'tel',
            defaultValue: '',
            rules: {
                validate: (value) => {
                    if (!value) return true
                    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
                    if (!match) {
                        return formatMessage({
                            defaultMessage: 'Please enter a valid birthday.',
                            id: 'use_registration_fields.error.invalid_format_date_new'
                        })
                    }

                    const year = parseInt(match[1], 10)
                    const month = parseInt(match[2], 10)
                    const day = parseInt(match[3], 10)

                    if (year < 1900 || year > 2099) {
                        return formatMessage({
                            defaultMessage: 'Year must be between 1900 and 2099.',
                            id: 'use_registration_fields.error.invalid_year'
                        })
                    }

                    // Basic calendar sanity check
                    if (month > 12 || day > 31) {
                        return formatMessage({
                            defaultMessage: 'Invalid month or day.',
                            id: 'use_registration_fields.error.invalid_date'
                        })
                    }

                    const enteredDate = new Date(value)
                    const today = new Date()

                    if (enteredDate.getTime() > today.getTime()) {
                        return formatMessage({
                            defaultMessage: 'Birthday cannot be in the future.',
                            id: 'use_registration_fields.error.future_date_not_allowed_future'
                        })
                    }

                    return true
                }
            },
            error: errors[`${prefix}birthday`],
            control
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Password',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            autoComplete: 'new-password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please create a password.',
                    id: 'use_registration_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'use_registration_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'use_registration_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'use_registration_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'use_registration_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'use_registration_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}password`],
            control
        },
        confirmPassword: {
            name: `${prefix}confirmPassword`,
            label: formatMessage({
                defaultMessage: 'Confirm Password',
                id: 'use_registration_fields.label.confirm_password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please confirm password.',
                    id: 'use_registration_fields.error.required_confirm_password'
                }),
                validate: (value) =>
                    value === watch(`${prefix}password`, '') ||
                    formatMessage({
                        defaultMessage: 'Passwords do not match',
                        id: 'use_registration_fields.error.passwords_do_not_match'
                    })
            },
            error: errors[`${prefix}confirmPassword`],
            control
        },
        recaptcha: {
            name: `${prefix}recaptcha`,
            type: 'hidden', // or just omit in UI
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please verify that you are not a robot.',
                    id: 'use_registration_fields.error.invalid_recaptcha_message'
                }),
                validate: (value) => {
                    return (
                        (value && value.length > 0) ||
                        formatMessage({
                            defaultMessage: 'Please verify that you are not a robot.',
                            id: 'use_registration_fields.error.invalid_recaptcha_message'
                        })
                    )
                }
            },
            error: errors[`${prefix}recaptcha`],
            control
        },
        acceptsMarketing: {
            name: `${prefix}acceptsMarketing`,
            label: formatMessage({
                defaultMessage:
                    'Sign me up for Salesforce emails (you can unsubscribe at any time)',
                id: 'use_registration_fields.label.sign_up_to_emails'
            }),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}acceptsMarketing`],
            control
        }
    }

    return fields
}
